// TelcoTrack — printable D5 occupancy HTML. MUK / RAY-TR3 = physical cabinet sheets.
// Project Drive artifact is Excel + PDF, not this HTML.

import { renderRozvadecOccupancyFiberDiagram } from './rozvadec-occupancy-schema.diagram';
import { occupancySheetPositionPorts } from './rozvadec-occupancy-schema.sheets';
import { buildRayTr3D5Rows } from './rozvadec-occupancy-schema.raytr3';
import type {
  RozvadecOccupancyDoorSplitter,
  RozvadecOccupancyGenerateResult,
  RozvadecOccupancyPortCell,
  RozvadecOccupancySheet,
} from './rozvadec-occupancy-schema.types';
import { emptyRsuOccupancy } from './rozvadec-rsu-occupancy';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function statusClass(status: string): string {
  if (status === 'obsazeno') return 'seat-occ';
  if (status === 'rez.') return 'seat-rez';
  if (status === 'plán') return 'seat-plan';
  return 'seat-free';
}

function ofsmGridHtml(ports: readonly RozvadecOccupancyPortCell[], columns: number): string {
  if (ports.length === 0) return '';
  const rows = 12;
  const cells: string[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < columns; col += 1) {
      const port = ports[col * rows + row];
      if (!port || port.status === 'neexistuje') continue;
      cells.push(
        `<span class="chip ${statusClass(port.status)}" title="${escapeHtml(port.hopRef ?? '')}">${escapeHtml(port.printLabel)} ${escapeHtml(port.target)}</span>`,
      );
    }
    cells.push('<br>');
  }
  return `<div class="ofsm-grid cols-${columns}">${cells.join('')}</div>`;
}

function portChips(ports: readonly RozvadecOccupancyPortCell[], field: 'printLabel' | 'target'): string {
  if (ports.length === 0) return '';
  return ports
    .map((port) => {
      const text = field === 'target' ? port.target : port.printLabel;
      return `<span class="chip ${statusClass(port.status)}">${escapeHtml(text)}</span>`;
    })
    .join('');
}

function doorBlock(splitter: RozvadecOccupancyDoorSplitter): string {
  const outs = splitter.outputs
    .map((port) => {
      const odd = Number(port.printLabel.replace(/\D/g, '')) % 2 === 1;
      return `<span class="chip ${statusClass(port.status)} ${odd ? 'chip-odd' : 'chip-even'}">${escapeHtml(port.printLabel)}${port.target !== 'volno' ? ` ${escapeHtml(port.target)}` : ''}</span>`;
    })
    .join('');
  return `<article class="door-spl">
    <header>${escapeHtml(splitter.printLabel)} ${escapeHtml(splitter.ratio)}</header>
    <p class="door-in">${escapeHtml(splitter.input.hopRef ?? splitter.input.printLabel)}</p>
    <div class="door-outs">${outs}</div>
  </article>`;
}

function sheetHtml(sheet: RozvadecOccupancySheet): string {
  const role = sheet.networkRole === 'none' ? '—' : sheet.networkRole;
  const side = sheet.sideLabel ? ` · ${sheet.sideLabel}` : '';
  const rows = sheet.positions
    .filter((slot) => slot.render)
    .map((slot) => {
      const { labels, targets } = occupancySheetPositionPorts(slot);
      const cassetteLabel = slot.empty
        ? slot.cassette?.printLabel ?? 'Volná pozice'
        : slot.cassette?.printLabel ?? '—';
      const route = slot.cassette?.routeLabel ?? '';
      const status = slot.empty
        ? 'volná-pozice'
        : slot.cassette?.kind === 'OCCUPIED_OPAQUE'
          ? 'obsazená-pozice'
          : slot.cassette?.kind === 'JUMPER'
            ? 'volno'
            : slot.cassette?.ports.some((item) => item.status === 'obsazeno')
              ? 'obsazeno'
              : slot.cassette?.ports.every((item) => item.status === 'rez.')
                ? 'rez.'
                : 'volno';
      const scale = slot.scaleLabels.join('<br>');
      const ofsmGrid =
        slot.cassette?.kind === 'OFSM' && (slot.cassette.columnCount ?? 0) > 0
          ? ofsmGridHtml(slot.cassette.ports, slot.cassette.columnCount ?? 12)
          : '';
      return `<tr class="${statusClass(status)} ${slot.scaleLabels.length > 1 ? 'span2' : ''}">
        <th class="pos">${scale}</th>
        <td class="cas">${escapeHtml(cassetteLabel)}</td>
        <td class="ports" colspan="${sheet.kind === 'ray-stand' ? 2 : 1}">${ofsmGrid || portChips(labels, 'printLabel')}</td>
        ${sheet.kind === 'ray-stand' ? '' : `<td class="tgts">${portChips(targets, 'target')}</td>`}
        <td class="route">${escapeHtml(route)}</td>
      </tr>`;
    })
    .join('');
  const doors = sheet.doorSplitters.map(doorBlock).join('');
  const refs = sheet.references.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  const notes = sheet.notes.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  return `<section class="d5-sheet" data-page="${sheet.pageFormat}">
    <header class="d5-head">
      <div><strong>${escapeHtml(sheet.cabinetTypeLabel)}</strong> ${escapeHtml(sheet.configuration)}</div>
      <div>SAP ${escapeHtml(sheet.sapCode)}</div>
      <div>${role === '—' ? 'Funkce bodu —' : `Funkce bodu <strong>${escapeHtml(role)}</strong>`}</div>
      <div>${escapeHtml(sheet.objectName)}</div>
      <div>${escapeHtml(sheet.locality)}</div>
      <div>${escapeHtml(sheet.rowLabel)}${escapeHtml(side)} · přední pohled</div>
      <div>List ${sheet.sheetIndex} / ${sheet.sheetCount}</div>
    </header>
    <div class="d5-body">
      <table class="d5-rack">
        <thead>
          <tr>
            <th>Pozice</th>
            <th>Kazeta</th>
            <th>Porty / svary</th>
            <th>Cíl</th>
            <th>Typ / trasa / kabel</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      ${sheet.kind === 'muk-row' ? `<aside class="d5-door">
        <h3>Splittery ve dveřích</h3>
        ${doors || '<p class="muted">Žádný 1:32 ve dveřích</p>'}
      </aside>` : ''}
    </div>
    <footer class="d5-foot">
      <div>
        <h3>Odkazy</h3>
        <ul>${refs || '<li>Žádné odkazy</li>'}</ul>
      </div>
      <div>
        <h3>Legenda</h3>
        <p><span class="chip seat-occ">obsazeno</span> <span class="chip seat-rez">rez.</span> <span class="chip seat-free">volno</span> <span class="chip seat-plan">plán</span></p>
        <ul>${notes}</ul>
      </div>
    </footer>
  </section>`;
}

function blockerList(result: RozvadecOccupancyGenerateResult): string {
  if (result.exportable) return '';
  const items = result.blockers
    .map((item) => `<li><code>${escapeHtml(item.code)}</code> — ${escapeHtml(item.message)}</li>`)
    .join('');
  return `<section><h2>Export je blokovaný</h2><ul>${items}</ul></section>`;
}

const PHYSICAL_CSS = `
    body { font-family: sans-serif; color: #111; margin: 1rem; background: #fff; }
    @page { size: A3 landscape; }
    .d5-sheet[data-page="A3-portrait"] { }
    .d5-sheet { break-after: page; margin-bottom: 2rem; border: 1px solid #111; }
    .d5-rack .span2 .pos { height: 3.2rem; }
    .ofsm-grid { line-height: 1.35; }
    .d5-head { display: grid; grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr)); gap: 0.35rem 1rem; background: #c8e6c9; border-bottom: 1px solid #111; padding: 0.6rem 0.75rem; font-size: 13px; }
    .d5-body { display: grid; grid-template-columns: minmax(0, 1fr) 14rem; }
    .d5-sheet[data-page="A3-portrait"] .d5-body { grid-template-columns: 1fr; }
    .d5-rack { border-collapse: collapse; width: 100%; font-size: 11px; }
    .d5-rack th, .d5-rack td { border: 1px solid #111; padding: 0.2rem 0.3rem; vertical-align: top; }
    .d5-rack thead th { background: #c8e6c9; }
    .d5-rack .pos { width: 3rem; background: #c8e6c9; text-align: center; }
    .chip { display: inline-block; border: 1px solid #111; padding: 0 0.25rem; margin: 0 0.1rem 0.1rem 0; font-size: 10px; }
    .seat-occ { background: #bbdefb; }
    .seat-rez, .seat-free { background: #eee; color: #444; }
    .seat-plan { background: #fff; }
    .ports .chip { color: #b71c1c; }
    .d5-door { border-left: 1px solid #111; padding: 0.5rem; background: #fafafa; }
    .door-spl { border: 1px solid #111; margin-bottom: 0.5rem; padding: 0.35rem; }
    .door-spl header { background: #c8e6c9; margin: -0.35rem -0.35rem 0.35rem; padding: 0.2rem 0.35rem; color: #b71c1c; font-weight: 700; }
    .d5-foot { border-top: 1px solid #111; display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; padding: 0.6rem 0.75rem; font-size: 11px; }
    .muted { color: #666; }
    table { border-collapse: collapse; margin: 0 0 1.5rem; width: min(720px, 100%); }
    th, td { border: 1px solid #ccc; padding: 0.35rem 0.5rem; text-align: left; }
    th { background: #f3f4f6; }
    .meta { color: #444; margin-bottom: 1rem; }
    .fiber-schema svg { display: block; max-width: 100%; height: auto; }
    .fiber-schema .fiber-line { fill: none; stroke-width: 2.25; stroke-linecap: round; }
    .fiber-schema .fiber-block { fill: #f8fafc; stroke: #111; stroke-width: 1.25; }
    .fiber-schema .fiber-caption,
    .fiber-schema .fiber-end { writing-mode: horizontal-tb; transform: none; direction: ltr; }
    .fiber-schema .fiber-caption { font-size: 11px; font-weight: 700; fill: #111; }
    .fiber-schema .fiber-end { font-size: 10px; fill: #444; }
`;

export function renderRozvadecOccupancySchemaHtml(result: RozvadecOccupancyGenerateResult): string {
  const { schema } = result;
  const title = `Schéma osazení D5 — ${schema.printName}`;
  if (schema.sheets.length > 0) {
    return `<!doctype html>
<html lang="cs">
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(title)}</title>
  <style>${PHYSICAL_CSS}</style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <p class="meta">Renderer ${escapeHtml(schema.rendererId)} · ${escapeHtml(schema.cabinetCode)} · SAP ${escapeHtml(schema.sapCode ?? '—')} · ${escapeHtml(schema.configuration ?? '')}</p>
  ${blockerList(result)}
  ${schema.sheets.map(sheetHtml).join('')}
</body>
</html>`;
  }
  if (schema.rendererId === 'D5-RAYTR3') {
    const rows = buildRayTr3D5Rows(schema.rsuOccupancy ?? emptyRsuOccupancy(), schema.printName);
    const body = rows
      .map(
        (row) =>
          `<tr><td>${escapeHtml(String(row.odfOrder))}</td><td>${escapeHtml(row.odfType)}</td><td>${escapeHtml(row.moduleSap)}</td><td>${escapeHtml(String(row.position))}</td><td>${escapeHtml(row.connectorType)}</td><td>${escapeHtml(row.cableName)}</td><td>${escapeHtml(row.physicalFiber)}</td><td>${escapeHtml(row.logicalFiber)}</td><td>${escapeHtml(row.destination)}</td><td>${escapeHtml(row.status)}</td></tr>`,
      )
      .join('');
    return `<!doctype html><html lang="cs"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>${PHYSICAL_CSS}</style></head><body><h1>${escapeHtml(title)}</h1><p>${escapeHtml(schema.rows[0]?.label ?? 'RAY-TR3')}</p><table><thead><tr><th>Pořadí ODF</th><th>Typ ODF</th><th>SAP</th><th>Pozice</th><th>Konektor</th><th>Název OK</th><th>Fyzické</th><th>Logické</th><th>Směr/cíl</th><th>Stav</th></tr></thead><tbody>${body || '<tr><td colspan="10">Prázdný stojan</td></tr>'}</tbody></table>${blockerList(result)}</body></html>`;
  }
  if (schema.rendererId !== 'D5-PON32') {
    return `<!doctype html><html lang="cs"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title></head><body><p>Renderer ${escapeHtml(schema.rendererId)} je v této vlně odložený (ORSM / spojky / Huawei).</p>${blockerList(result)}</body></html>`;
  }

  return `<!doctype html>
<html lang="cs">
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(title)}</title>
  <style>${PHYSICAL_CSS}</style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <p class="meta">Renderer ${escapeHtml(schema.rendererId)} · SAP ${escapeHtml(schema.sapCode ?? '—')} · typ ${escapeHtml(schema.typeId ?? '—')}</p>
  ${blockerList(result)}
  ${renderRozvadecOccupancyFiberDiagram(schema)}
</body>
</html>`;
}

export function occupancySchemaDownloadFileName(objectId: string, rendererId: string): string {
  const safe = objectId.replace(/[^a-zA-Z0-9_-]/g, '');
  return `D5-osazeni-${rendererId}-${safe || 'rozvadec'}.html`;
}

